import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { UploadCloud, FileSearch, Loader2, AlertTriangle } from "lucide-react";

const MAX_SIZE = 50 * 1024 * 1024;
const ACCEPTED_EXT = [".ips", ".panic", ".synapse", ".crash", ".log", ".txt"];

export default function Uploader({ onResult, isLoading, setIsLoading, firstName }) {
  const { t, i18n } = useTranslation();
  const [error, setError] = useState("");
  const lang = i18n.resolvedLanguage || "es";

  const onDrop = useCallback(
    async (acceptedFiles, fileRejections) => {
      setError("");
      if (fileRejections && fileRejections.length > 0) {
        const code = fileRejections[0]?.errors?.[0]?.code;
        if (code === "file-too-large") setError(t("uploader.errorTooBig"));
        else if (code === "file-invalid-type") setError(t("uploader.invalidType"));
        else setError(t("uploader.errorGeneric"));
        return;
      }
      const file = acceptedFiles[0];
      if (!file) return;
      if (file.size === 0) {
        setError(t("uploader.errorEmpty"));
        return;
      }
      if (!navigator.onLine) {
        setError(t("uploader.offline"));
        return;
      }

      setIsLoading(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("lang", (lang || "es").slice(0, 2));
        if (firstName) fd.append("name", firstName);
        const backend = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${backend}/api/diagnose`, {
          method: "POST",
          body: fd,
        });
        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          let msg = t("uploader.errorGeneric");
          try {
            const j = JSON.parse(txt);
            if (j?.detail) msg = j.detail;
          } catch {
            /* noop */
          }
          throw new Error(msg);
        }
        const json = await res.json();
        onResult(json);
      } catch (e) {
        setError(e.message || t("uploader.errorGeneric"));
      } finally {
        setIsLoading(false);
      }
    },
    [onResult, setIsLoading, t, lang, firstName],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    multiple: false,
    maxSize: MAX_SIZE,
    accept: {
      "text/plain": ACCEPTED_EXT,
      "application/octet-stream": ACCEPTED_EXT,
    },
    noClick: true,
    noKeyboard: false,
  });

  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl bg-card/70 border border-border/70 p-10 sm:p-14 text-center gradient-border"
        data-testid="uploader-loading"
      >
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-primary/20 blur-2xl" />
            <Loader2 className="w-12 h-12 text-primary animate-spin relative" />
          </div>
          <h3 className="text-xl font-bold">{t("uploader.analyzing")}</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            {t("uploader.analyzingHint")}
          </p>
          <p className="text-xs text-muted-foreground/70 max-w-md">
            {t("uploader.analyzingLong")}
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      {...getRootProps()}
      className={`relative rounded-3xl border-2 border-dashed p-10 sm:p-14 text-center transition cursor-pointer group ${
        isDragActive
          ? "border-primary bg-primary/5"
          : "border-border/70 hover:border-primary/60 bg-card/40"
      }`}
      data-testid="uploader-dropzone"
      onClick={() => open()}
    >
      <input {...getInputProps()} data-testid="uploader-file-input" />
      <div className="flex flex-col items-center gap-4">
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-primary/15 blur-2xl group-hover:bg-primary/30 transition" />
          <div className="relative h-16 w-16 rounded-2xl bg-secondary flex items-center justify-center ring-1 ring-white/5">
            <UploadCloud className="w-8 h-8 text-primary" />
          </div>
        </div>
        <div className="space-y-1.5">
          <h3 className="text-xl sm:text-2xl font-bold">{t("uploader.drop")}</h3>
          <p className="text-sm text-muted-foreground">{t("uploader.or")}</p>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            open();
          }}
          data-testid="uploader-browse-button"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-primary text-primary-foreground font-semibold hover:brightness-110 transition shadow-lg shadow-primary/20"
        >
          <FileSearch className="w-4 h-4" />
          {t("uploader.browse")}
        </button>
        <p className="text-xs text-muted-foreground/80 mt-1">
          {t("uploader.formats")}
        </p>

        {error && (
          <div
            className="flex items-start gap-2 mt-3 max-w-md text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2"
            data-testid="uploader-error"
          >
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span className="text-left">{error}</span>
          </div>
        )}
      </div>
    </motion.div>
  );
}
